import { useState, useEffect } from "react";
import { Star, Quote } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Real Google Reviews from Prime Baths of New Mexico
const REAL_REVIEWS = [
  {
    id: "1",
    author: "Barry Lane",
    rating: 5,
    text: "Shawn did an excellent job installing our bathtub. He replaced a jetted tub that wasn't working. He was very professional and kept us informed during the entire process. This is the 2nd time we have used Prime Baths and both experiences have been great!",
    date: "4 weeks ago",
    service: "bathtub-installation"
  },
  {
    id: "2",
    author: "Cassandra Cadwell",
    rating: 5,
    text: "They installed my bath/shower in one day and it looks great! The installers were professional and the bathroom was left clean. I would recommend them highly.",
    date: "11 months ago",
    service: "bath-shower-installation"
  },
  {
    id: "3",
    author: "J Mon",
    rating: 5,
    text: "Very happy with my new shower. My installer, Shawn, answered all my questions, explaining the process and approximate time of completion. Job was done in one day and looks amazing. Shawn also installed the grab bars I had purchased.",
    date: "2 months ago",
    service: "shower-installation"
  },
  {
    id: "4",
    author: "Dana Golden",
    rating: 5,
    text: "I had a wonderful experience with Prime Baths. They answered all my questions quickly. Shawn did an amazing job with the install and clean up. So happy with my beautiful new shower!",
    date: "3 months ago",
    service: "shower-install"
  },
  {
    id: "5",
    author: "Jimmy Wightman",
    rating: 5,
    text: "Gene came out to talk about the project. He was not pushy and answered all my questions. Shawn is the best installer in the industry. Great experience all around, would definitely recommend to my friends and family.",
    date: "2 months ago",
    service: "bathtub-installation"
  }
];

export const Testimonials = ({ filterByService = null }) => {
  const [reviews, setReviews] = useState(REAL_REVIEWS);
  const [loading, setLoading] = useState(false);

  const displayReviews = filterByService 
    ? reviews.filter(r => r.service?.includes(filterByService) || true).slice(0, 3)
    : reviews.slice(0, 3);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-5 w-5 ${i < rating ? "fill-amber-400 text-amber-400" : "text-gray-300"}`}
      />
    ));
  };

  return (
    <section className="py-20 bg-[#F9FAFB]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <span className="text-[#1E4C9A] font-semibold text-sm uppercase tracking-wider">
            Customer Reviews
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4 font-['Playfair_Display']" data-testid="testimonials-title">
            Trusted by New Mexico Homeowners
          </h2>
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex">{renderStars(5)}</div>
            <span className="text-gray-700 font-semibold">4.9/5</span>
            <span className="text-gray-500">from 250+ Google Reviews</span>
          </div>
          <div className="section-divider mx-auto" />
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayReviews.map((review, index) => (
            <div
              key={review.id}
              className={`review-card bg-white p-8 rounded-xl shadow-sm border border-gray-100 animate-slide-up delay-${(index + 1) * 100}`}
              data-testid={`testimonial-card-${index}`}
            >
              <Quote className="h-8 w-8 text-[#1E4C9A]/20 mb-4" />
              <p className="text-gray-700 mb-6 leading-relaxed">
                "{review.text}"
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{review.author}</p>
                  <p className="text-sm text-gray-500">{review.date}</p>
                </div>
                <div className="flex">{renderStars(review.rating)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Google Badge */}
        <div className="mt-12 text-center">
          <a 
            href="https://www.google.com/search?q=Prime+Baths+of+New+Mexico+reviews" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-full shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <svg className="h-6 w-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-gray-700 font-medium">See All 250+ Google Reviews</span>
          </a>
        </div>
      </div>
    </section>
  );
};
